<template>
    <div class="mt-5">
        <label class="block allround-label">
            {{ titleText }}
        </label>

        <input
            ref="fileInputRef"
            type="file"
            accept="image/*,video/*,audio/*"
            multiple
            class="hidden"
            @change="handleInputChange"
        />

        <div
            class="allround-panel"
            :class="{ 'allround-panel--busy': busy }"
            @dragover.prevent
            @drop.prevent="handleDrop"
        >
            <button
                v-if="!items.length"
                type="button"
                class="allround-empty-state group"
                :disabled="busy"
                @click="openPicker"
            >
                <span class="allround-empty-state__box">
                    <ui-upload-icon :hoverable="true" />
                    <span class="allround-empty-state__title">
                        {{ emptyTitleText }}
                    </span>
                </span>
            </button>

            <div v-else class="allround-grid">
                <div
                    v-for="(item, index) in items"
                    :key="item.id"
                    class="allround-card"
                    :class="{
                        'is-dragging': dragFromIndex === index,
                        'is-drag-over':
                            dragOverIndex === index && dragFromIndex !== index,
                    }"
                    :data-allround-index="index"
                    :draggable="canReorderItems && !item.loading"
                    :style="
                        canReorderItems && !item.loading
                            ? { touchAction: 'none' }
                            : {}
                    "
                    @dragstart="handleCardDragStart(index, $event)"
                    @dragover="handleCardDragOver(index, $event)"
                    @drop="handleCardDrop(index, $event)"
                    @dragend="resetCardDragState"
                    @touchstart="handleCardTouchStart(index, $event)"
                    @touchmove="handleCardTouchMove"
                    @touchend="handleCardTouchEnd"
                    @touchcancel="resetCardDragState"
                >
                    <button
                        type="button"
                        class="allround-card__remove"
                        @mousedown.stop
                        @touchstart.stop
                        @click.stop="removeReferenceAt(index)"
                    >
                        ×
                    </button>
                    <div v-if="item.loading" class="allround-card__loading">
                        <span class="allround-spinner"></span>
                    </div>
                    <template v-else-if="item.kind === 'image'">
                        <img
                            v-if="item.url"
                            :src="item.url"
                            :alt="item.name || 'reference image'"
                            class="allround-card__media"
                        />
                    </template>
                    <template v-else-if="item.kind === 'video'">
                        <video
                            v-if="item.url"
                            :src="item.url"
                            class="allround-card__media"
                            muted
                            playsinline
                            preload="metadata"
                        ></video>
                    </template>
                    <template v-else>
                        <div class="allround-card__audio">
                            <span class="allround-card__audio-icon">♪</span>
                            <span class="allround-card__audio-name">
                                {{ item.name || audioLabel }}
                            </span>
                        </div>
                    </template>
                    <div class="allround-card__footer">
                        <span>{{ getItemKindLabel(item.kind) }}</span>
                        <span v-if="item.duration">
                            {{ formatDuration(item.duration) }}
                        </span>
                    </div>
                </div>

                <button
                    type="button"
                    class="allround-add-card group"
                    :disabled="busy"
                    @click="openPicker"
                >
                    <ui-upload-icon :hoverable="true" />
                    <span class="allround-add-card__text">
                        {{ addMoreText }}
                    </span>
                </button>
            </div>

            <p class="allround-panel__hint">
                {{ hintText }}
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from '~/composables/useI18n'
import { showLoginModal } from '~/utils/emitter'
import { useUserStore } from '~/stores/user'
import {
    formatAlmightyDurationSeconds,
    type AlmightyReferenceItem,
} from '~/utils/almightyReference'
import { isDance25Model } from '~/utils/videoModelCapabilities'
import { useSeedanceAllroundReferences } from '~/composables/useSeedanceAllroundReferences'

const items = defineModel<AlmightyReferenceItem[]>('items', {
    default: () => [],
})
const busy = defineModel<boolean>('busy', {
    default: false,
})
const props = defineProps<{ modelKey?: string }>()

const { t, locale } = useI18n()
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)
const isZh = computed(() => locale.value.toLowerCase().startsWith('zh'))
const dragFromIndex = ref<number | null>(null)
const dragOverIndex = ref<number | null>(null)

const canReorderItems = computed(() => {
    return items.value.length > 1 && !items.value.some(item => item.loading)
})

const {
    fileInputRef,
    hasBusyItems,
    openPicker,
    handleInputChange,
    handleDrop,
    removeReferenceAt,
    getItemKindLabel,
} = useSeedanceAllroundReferences({
    items,
    isLoggedIn,
    locale,
    tipTitle: t('common.tip'),
    modelKey: computed(() => props.modelKey),
    onRequireLogin: () => {
        showLoginModal()
    },
})

const titleText = computed(() => {
    return isZh.value ? '参考内容' : 'Reference content'
})

const hintText = computed(() => {
    if (isDance25Model(props.modelKey)) {
        return isZh.value
            ? '支持上传图片最多30张、视频最多10个、音频最多10个（单个2-30秒，各类总时长≤30秒）'
            : 'Up to 30 images, 10 videos and 10 audios. Each video/audio must be 2-30s, with total duration within 30s.'
    }
    return isZh.value
        ? '支持上传图片1-9张、视频最多3个、音频最多3个（单个2-15秒，各类总时长≤15秒）'
        : 'Mix image, video and audio references. Up to 9 images, 3 videos and 3 audios. Each video/audio must be 2-15s, with total duration within 15s. Audio references require at least one image or video.'
})

const emptyTitleText = computed(() => {
    return isZh.value ? '上传参考' : 'Upload reference'
})

const addMoreText = computed(() => {
    return isZh.value ? '添加' : 'Add'
})

const audioLabel = computed(() => {
    return isZh.value ? '音频参考' : 'Audio reference'
})

const formatDuration = (duration?: number) => {
    return formatAlmightyDurationSeconds(duration)
}

const resetCardDragState = () => {
    dragFromIndex.value = null
    dragOverIndex.value = null
}

const moveReferenceItem = (from: number | null, to: number | null) => {
    if (from === null || to === null) {
        resetCardDragState()
        return
    }
    if (
        from < 0 ||
        to < 0 ||
        from >= items.value.length ||
        to >= items.value.length ||
        from === to
    ) {
        resetCardDragState()
        return
    }

    const nextItems = [...items.value]
    const [moved] = nextItems.splice(from, 1)
    if (!moved) {
        resetCardDragState()
        return
    }
    nextItems.splice(to, 0, moved)
    items.value = nextItems
    resetCardDragState()
}

const isInternalCardDrag = (event: DragEvent) => {
    return Array.from(event.dataTransfer?.types || []).includes(
        'application/x-almighty-reorder'
    )
}

const handleCardDragStart = (index: number, event: DragEvent) => {
    if (!canReorderItems.value || items.value[index]?.loading) {
        event.preventDefault()
        return
    }
    dragFromIndex.value = index
    dragOverIndex.value = index
    event.dataTransfer?.setData('application/x-almighty-reorder', String(index))
    if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
    }
}

const handleCardDragOver = (index: number, event: DragEvent) => {
    if (!canReorderItems.value || !isInternalCardDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    dragOverIndex.value = index
    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'move'
    }
}

const handleCardDrop = (index: number, event: DragEvent) => {
    if (!canReorderItems.value || !isInternalCardDrag(event)) return
    event.preventDefault()
    event.stopPropagation()
    moveReferenceItem(dragFromIndex.value, index)
}

const getTouchTargetIndex = (event: TouchEvent) => {
    const touch = event.touches[0] || event.changedTouches[0]
    if (!touch) return null
    const target = document.elementFromPoint(touch.clientX, touch.clientY)
    if (!(target instanceof Element)) return null
    const card = target.closest('[data-allround-index]')
    const index = Number((card as HTMLElement | null)?.dataset.allroundIndex)
    return Number.isInteger(index) ? index : null
}

const handleCardTouchStart = (index: number, event: TouchEvent) => {
    if (!canReorderItems.value || items.value[index]?.loading) return
    dragFromIndex.value = index
    dragOverIndex.value = index
    event.preventDefault()
}

const handleCardTouchMove = (event: TouchEvent) => {
    if (dragFromIndex.value === null || !canReorderItems.value) return
    const index = getTouchTargetIndex(event)
    if (index !== null) {
        dragOverIndex.value = index
    }
    event.preventDefault()
}

const handleCardTouchEnd = (event: TouchEvent) => {
    if (dragFromIndex.value === null) return
    const index = getTouchTargetIndex(event)
    moveReferenceItem(dragFromIndex.value, index ?? dragOverIndex.value)
}

watch(
    () => hasBusyItems.value,
    nextVal => {
        busy.value = nextVal
    },
    {
        immediate: true,
    }
)
</script>

<style scoped lang="less">
.allround-panel {
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    background: #1c1c20;
    padding: 10px;
    min-height: 120px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
}

.allround-label {
    display: block;
    margin-bottom: 10px;
    color: #fff;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
}

.allround-panel--busy {
    opacity: 0.72;
}

.allround-empty-state {
    width: 100%;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    color: #fff;
}

.allround-empty-state__box {
    width: 100px;
    height: 100px;
    border-radius: 10px;
    border: 1px dashed rgba(255, 255, 255, 0.2);
    padding: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    color: #fff;
    transition: all 0.2s ease;
}

.allround-empty-state:hover:not(:disabled) .allround-empty-state__box,
.allround-add-card:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.3);
}

.allround-empty-state__title {
    font-size: 12px;
    font-weight: 400;
    color: #adadad;
    line-height: 1.4;
    text-align: center;
}

.allround-grid {
    width: 100%;
    display: flex;
    gap: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 0;
}

.allround-card {
    position: relative;
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: #1c1c20;
    transition:
        border-color 0.2s ease,
        opacity 0.2s ease,
        transform 0.2s ease;
}

.allround-card[draggable='true'] {
    cursor: grab;
}

.allround-card.is-dragging {
    cursor: grabbing;
    opacity: 0.55;
    transform: scale(0.98);
}

.allround-card.is-drag-over {
    border-color: rgba(153, 247, 104, 0.85);
}

.allround-add-card {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    border-radius: 10px;
    border: 1px dashed rgba(255, 255, 255, 0.2);
    background: rgba(0, 0, 0, 0.3);
    color: #adadad;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.2s ease;
}

.allround-add-card__text {
    font-size: 12px;
    color: #adadad;
    line-height: 1.4;
    text-align: center;
}

.allround-card__remove {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
    width: 20px;
    height: 20px;
    border-radius: 9999px;
    background: #2e2e2e;
    color: white;
    font-size: 14px;
    line-height: 1;
}

.allround-card__media,
.allround-card__audio,
.allround-card__loading {
    width: 100%;
    height: 100%;
}

.allround-card__media {
    object-fit: cover;
    display: block;
    background: rgba(255, 255, 255, 0.02);
}

.allround-card__audio,
.allround-card__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: white;
}

.allround-card__audio {
    padding: 12px 10px;
}

.allround-card__audio-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.06);
    font-size: 14px;
    font-weight: 700;
}

.allround-card__audio-name {
    max-width: 100%;
    font-size: 11px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.68);
    text-align: center;
    word-break: break-word;
}

.allround-card__footer {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 8px;
    font-size: 10px;
    color: white;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.72) 100%
    );
}

.allround-panel__hint {
    width: 100%;
    margin: 0;
    padding: 0;
    color: #adadad;
    font-size: 12px;
    font-weight: 400;
    line-height: 1.5;
    text-align: center;
}

.allround-spinner {
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    border: 2px solid rgba(255, 255, 255, 0.22);
    border-top-color: rgba(255, 255, 255, 0.92);
    animation: allround-spin 0.8s linear infinite;
}

@keyframes allround-spin {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
